import React, { useEffect, useState } from "react";
import { Badge } from "components/UI/Badge/Badge";
import {
  CheckIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { Trans } from "@lingui/macro";
import { InputTextSlim } from "components/UI/InputTextSlim/InputTextSlim";
import { useInputChange } from "hooks/useInputChange";
import { useDispatch } from "react-redux";
import {
  settingsAddCallbackSagaAC,
  settingsDeleteCallbackSagaAC,
  settingsEditUrlCallbackSagaAC,
} from "model/dashboardSettings/settingsActionCreators";

// Type
export type typeCallbackListItem = {
  id: string | null | undefined;
  url: string | null | undefined;
  callbackType: "ORDER" | "SALE" | "REFUND" | "Z_REPORT";
};

// Component
export const CallbackListItem: React.FC<typeCallbackListItem> = ({
  id,
  url,
  callbackType,
}) => {
  const urlRegPattern =
    // eslint-disable-next-line
    /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()!@:%_\+.~#?&\/\/=]*)$/;

  const dispatch = useDispatch();

  const [isEditing, setEditing] = useState(false);
  const [isAdding, setAdding] = useState(false);
  const {
    value: urlValue,
    changeValueHandler: urlChangeValueHandler,
    setValue: urlSetValue,
  } = useInputChange(url ? url : "");
  const [isValidUrl, setValidUrl] = useState(false);

  useEffect(() => {
    if (urlRegPattern.test(urlValue)) setValidUrl(true);
    else setValidUrl(false);

    // eslint-disable-next-line
  }, [urlValue]);

  const reference = React.useRef(null);

  const toEdit = () => {
    // @ts-ignore
    if (reference.current) reference.current.focus();

    urlSetValue(url!);
    setEditing(true);
    setAdding(false);
  };

  const toAdd = () => {
    // @ts-ignore
    if (reference.current) reference.current.focus();

    urlSetValue("");
    setAdding(true);
    setEditing(false);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isValidUrl) {
      if (isAdding) {
        const newUrl = urlValue.trim();
        if (newUrl) {
          dispatch(
            settingsAddCallbackSagaAC({
              eventType: callbackType,
              url: newUrl,
            }),
          );
        }
      }

      if (isEditing) {
        const newUrl = urlValue.trim();
        if (newUrl) {
          dispatch(settingsEditUrlCallbackSagaAC(id!, newUrl));
        }
      }

      setAdding(false);
      setEditing(false);
    }
  };

  const onDelete = () => {
    dispatch(settingsDeleteCallbackSagaAC(id!));
  };

  return (
    <div className="flex gap-x-2 items-center py-1 px-1.5 hover:bg-gray-100">
      <span>
        <Badge color="blue" message={callbackType} />
      </span>
      {isEditing || isAdding ? (
        <>
          <form
            onSubmit={(e) => onSubmit(e)}
            className="flex-grow flex items-center"
          >
            <div className="flex gap-x-2 flex-grow">
              <div className="flex-grow">
                <InputTextSlim
                  id={callbackType}
                  reference={reference}
                  onChange={urlChangeValueHandler}
                  value={urlValue}
                  inputClassName={`w-full ${isValidUrl ? "text-gray-600" : "text-red-600"}`}
                />
              </div>
              <span className="cursor-pointer flex flex-col justify-center pl-1">
                <button type="submit" className="focus:outline-none">
                  <CheckIcon className="h-5 text-gray-300 hover:text-gray-600" />
                </button>
              </span>
            </div>
          </form>
        </>
      ) : (
        <>
          <div className="flex gap-x-2 flex-grow">
            <span className="flex-grow">
              {url ? (
                url
              ) : (
                <span className="text-gray-400 text-sm cursor-default">
                  <Trans>Add callback url</Trans>
                </span>
              )}
            </span>
            <div className="flex gap-x-2">
              {url ? (
                <>
                  <span className="cursor-pointer" onClick={toEdit}>
                    <PencilIcon className="h-5 text-gray-300 hover:text-gray-600" />
                  </span>
                  <span className="cursor-pointer" onClick={onDelete}>
                    <TrashIcon className="h-5 text-gray-300 hover:text-gray-600" />
                  </span>
                </>
              ) : (
                <>
                  <span className="cursor-pointer" onClick={toAdd}>
                    <PlusIcon className="h-5 text-gray-300 hover:text-gray-600" />
                  </span>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
